import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import SeriesMetadataDisplay from './seriesMetadataDisplay'
import { AppContext } from '../../../../App'
import { useDispatch, useSelector } from 'react-redux'
import { selectMultiStrikeChartSecurities, selectMultiStrikeChartType, selectMultiStrikeCumOi, selectMultiStrikeShowChange } from '../../../../selectors/multiStrikeChart'
import SeriesOptionMetadataDisplay from './seriesOptionMetadataDisplay'
import { Box, Checkbox, FormControlLabel, Stack } from '@mui/material'
import { ChartViewKey } from '../../../../assets/constants'
import { selectMultiStrikeChartConfigChartType, selectMultiStrikeChartConfigCumOi, selectMultiStrikeChartConfigSecurities, selectMultiStrikeChartConfigShowChange } from '../../../../selectors/chart'
import { callColorCumulative, callColorPallet, putColorCumulative, putColorPallet } from '../../styles'
import { ChartSnapshotMultiStrikeChart } from '../../chart.typings'
import { getSecurityAsync } from '../../../../util/security'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { MultiStrikeChartTypes, MultiStrikeSecuritiesClassType, setMultiStrikeChartType, setMultiStrikeCumOi, setMultiStrikeShowChange } from '../../../../reducer/multiStrikeChart.reducer'
import { DataUpdateRequestType } from '../../../../util/chart'

export type ChartMetadataProps = {
    container: HTMLDivElement
    chartSnapshot: ChartSnapshotMultiStrikeChart
    hoverdTime: number
    viewKey: string
    multiStrikeClassType: MultiStrikeSecuritiesClassType
    displayChangeToggle?: boolean
    displayCumulativeToggle?: boolean
}

export const ChartMetadata: React.FC<ChartMetadataProps> = ({ container, chartSnapshot, hoverdTime, viewKey, multiStrikeClassType, displayChangeToggle = true, displayCumulativeToggle = true }) => {
    const [paneContainer, setPaneContainer] = useState<HTMLDivElement>()
    const appContext = useContext(AppContext)
    const chart = appContext.getMultiStrikeChart(viewKey)
    const dispatch = useDispatch()
    const [primarySecurity, setPrimarySecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { const k = chart?.primary ?? ''; if (k) getSecurityAsync(k).then(setPrimarySecurity) }, [chart?.primary])
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const multiStrikeChartSecurities = useSelector(isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities)
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumOi = useSelector(isWidget ? selectMultiStrikeChartConfigCumOi(viewKey) : selectMultiStrikeCumOi)
    const showChange = useSelector(isWidget ? selectMultiStrikeChartConfigShowChange(viewKey) : selectMultiStrikeShowChange)
    const [uniqueStrikes, strikeToSecurity] = useMemo(() => {
        const strikes = Object.keys(multiStrikeClassType ?? {})
        const strikeToSecurity: {
            [key: string]: {
                callSecurityKey: string | undefined
                putSecurityKey: string | undefined
                callPut: ['CE', 'PE']
            }
        } = {}
        strikes.forEach((strike) => {
            strikeToSecurity[strike] = {
                putSecurityKey: multiStrikeClassType?.[strike]?.putSecKey,
                callSecurityKey: multiStrikeClassType?.[strike]?.callSecKey,
                callPut: ['CE', 'PE']
            }
        })
        return [strikes, strikeToSecurity]
    }, [multiStrikeChartSecurities, multiStrikeClassType])

    const updatePaneContainer = useCallback(() => {
        if (!container) return
        let child = container.querySelector<HTMLDivElement>(`.chart-pane-container`)
        if (child) {
            setPaneContainer(child)
        } else {
            const child = document.createElement('div')
            child.classList.add('chart-pane-container')
            const containerStyle: Record<string, string> = {
                display: 'flex',
                'flex-direction': 'column',
                'z-index': '3',
                'justify-content': 'start',
                'align-items': 'start',
                width: '100%'
            }

            for (let key in containerStyle) {
                child.style.setProperty(key, containerStyle[key]!)
            }
            container.appendChild(child)
            setPaneContainer(child)
        }
    }, [container])

    useEffect(() => {
        updatePaneContainer()
    }, [updatePaneContainer])

    const handleShowChange = useCallback(
        (showChange: boolean) => {
            AnalyticsManager.logEvent('Change Toggled', {
                source: 'multistrike-chart',
                type: 'change',
                value: showChange
            })
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({ viewKey: viewKey, config: { showChange } }))
            } else {
                dispatch(setMultiStrikeShowChange(showChange))
            }
        },
        [viewKey, dispatch, isWidget]
    )

    useEffect(() => {
        if (!chart || !multiStrikeChartSecurities || !multiStrikeClassType) return
        const callPutSecurities: MultiStrikeSecuritiesClassType = {}
        Object.keys(multiStrikeChartSecurities).forEach((strike) => {
            callPutSecurities[strike] = {
                callPut: multiStrikeChartSecurities[strike],
                callSecKey: multiStrikeClassType?.[strike]?.callSecKey,
                putSecKey: multiStrikeClassType?.[strike]?.putSecKey
            }
        })
        if (chartType === 'cumOi') {
            chart.updateCumOiCallPutVisible()
        } else {
            chart.updateStrikeCallPutVisible(callPutSecurities)
        }
    }, [chart, multiStrikeChartSecurities, multiStrikeClassType, chartType])

    const handleChartTypeChange = useCallback(
        (checked: boolean) => {
            const chart = appContext.getMultiStrikeChart(viewKey)
            const chartType = checked ? ('cumOi' as MultiStrikeChartTypes) : ('oi' as MultiStrikeChartTypes)
            if (isWidget) {
                dispatch(updateMultiStrikeChartConfig({ viewKey: viewKey, config: { chartType, cumOi: checked } }))
            } else {
                dispatch(setMultiStrikeCumOi(checked))
                dispatch(setMultiStrikeChartType(chartType))
            }
            if (chart) {
                chart.chartType = chartType
                chart.onDataLoaded(DataUpdateRequestType.LIVE, true, false, false)
                chart.dataManager.fetchData({})
                chart.onCrosshairMove({} as any)
            }
            AnalyticsManager.logEvent('Chart Type Changed', { type: chartType, source: 'multistrike' })
        },
        [appContext, dispatch, isWidget, viewKey]
    )

    return (
        <>
            {paneContainer &&
                createPortal(
                    <>
                        <Box className="pane-meta-container disable-selection" sx={{ pointerEvents: 'all', background: (theme) => theme.palette.background.paper, top: 0, left: 0 }}>
                            <SeriesMetadataDisplay
                                viewKey={viewKey}
                                seriesData={chartSnapshot.get(chart?.dataManager.seriesApis[chart.primary]?.seriesApi as any) as any}
                                security={primarySecurity}
                                hoverdTime={hoverdTime}
                            />
                            {chartType === 'cumOi' ? (
                                <SeriesOptionMetadataDisplay
                                    strike={''}
                                    viewKey={viewKey}
                                    multiStrikeClassType={multiStrikeClassType}
                                    callSeriesData={chartSnapshot.get(chart?.dataManager.cumSeriesApis['cum_oi_CE']?.seriesApi as any) as any}
                                    putSeriesData={chartSnapshot.get(chart?.dataManager.cumSeriesApis['cum_oi_PE']?.seriesApi as any) as any}
                                    callSeriesColor={callColorCumulative}
                                    putSeriesColor={putColorCumulative}
                                    callKey={'cum_oi_CE'}
                                    putKey={'cum_oi_PE'}
                                    hoverdTime={hoverdTime}
                                />
                            ) : (
                                uniqueStrikes.map((strike, index) => {
                                    const callSeriesData = chartSnapshot.get(chart?.dataManager.seriesApis[strikeToSecurity?.[strike]?.callSecurityKey ?? '']?.seriesApi as any) as any
                                    const putSeriesData = chartSnapshot.get(chart?.dataManager.seriesApis[strikeToSecurity?.[strike]?.putSecurityKey ?? '']?.seriesApi as any) as any
                                    const callSeriesKey = callColorPallet[index]
                                    const putSeriesKey = putColorPallet[index]
                                    return (
                                        <SeriesOptionMetadataDisplay
                                            key={`${index}-multi-strike-series-meta-data`}
                                            viewKey={viewKey}
                                            strike={strike}
                                            callSeriesData={callSeriesData}
                                            putSeriesData={putSeriesData}
                                            callSeriesColor={callSeriesKey}
                                            putSeriesColor={putSeriesKey}
                                            callKey={strikeToSecurity?.[strike]?.callSecurityKey}
                                            putKey={strikeToSecurity?.[strike]?.putSecurityKey}
                                            hoverdTime={hoverdTime}
                                            multiStrikeClassType={multiStrikeClassType}
                                        />
                                    )
                                })
                            )}
                        </Box>
                        {chartType !== 'iv' && chartType !== 'price' && (
                            <Stack direction={isWidget ? 'column' : 'row'} mt={2} mr={9} gap={1} position="absolute" top={0} right={0} zIndex={3}>
                                {displayChangeToggle && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(event, checked) => {
                                                    handleShowChange(checked)
                                                }}
                                                checked={showChange}
                                            />
                                        }
                                        label="Change"
                                    />
                                )}
                                {displayCumulativeToggle && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={cumOi}
                                                onChange={(event, checked) => {
                                                    handleChartTypeChange(checked)
                                                }}
                                            />
                                        }
                                        label="Cum. OI"
                                    />
                                )}
                            </Stack>
                        )}
                    </>,
                    paneContainer
                )}
        </>
    )
}
